import { AudioEngineCoordinator } from './src/core/audio-engine-coordinator.js';
import { UIController } from './src/core/ui-controller.js';
import { TrackManager } from './src/core/track-manager.js';
import { AppStateManager } from './src/core/app-state-manager.js';
import { performanceMonitor } from './performance-monitor.js';
import { AUDIO_CONSTANTS } from './src/config/audio-constants.js';

// Get track URLs and names from constants
const TRACK_URLS = [...AUDIO_CONSTANTS.TRACK_URLS]; // Create mutable copy
const TRACK_NAMES = TRACK_URLS.map(url => {
  const filename = url.split('/').pop() || '';
  return filename.replace('.m4a', '').replace('.aac', '');
});

// Constants
const SAMPLE_RATE = AUDIO_CONSTANTS.SAMPLE_RATE;
// BEATS_PER_TRACK removed - using individual track metadata
const INITIAL_TEMPO = AUDIO_CONSTANTS.DEFAULT_TEMPO;

interface PerfMonInterface {
  start(): void;
  stop(): void;
  report(): void;
  reset(): void;
  poolStats(): Promise<void>;
}

/**
 * Refactored MultiTrackPlayer using composition
 */
class MultiTrackPlayer {
  private audioCoordinator: AudioEngineCoordinator;
  private uiController: UIController;
  private trackManager: TrackManager;
  private stateManager: AppStateManager;
  private audioContext: AudioContext | null = null;
  private wasmModule: WebAssembly.Module | null = null;

  constructor() {
    // Initialize components
    this.audioCoordinator = new AudioEngineCoordinator();
    this.trackManager = new TrackManager(TRACK_URLS.length);

    // Set up UI callbacks
    this.uiController = new UIController({
      onPlayToggle: this.handlePlayToggle.bind(this),
      onTempoChange: this.handleTempoChange.bind(this),
      onTrackMute: this.handleTrackMute.bind(this)
    });

    // Set up state manager callbacks
    this.stateManager = new AppStateManager({
      onBeatUpdate: beat => this.uiController.updateBeatDisplay(beat),
      onDebugUpdate: info => this.uiController.updateDebugCounters(info),
      onLevelUpdate: levels => this.uiController.updateLevelMeters(levels)
    });

    this.initializeUI();
  }

  /**
   * Initialize the application
   */
  async initialize(): Promise<void> {
    try {
      // Load WebAssembly module
      const wasmResponse = await fetch('./audio-engine/wasm/signalsmith-stretch.wasm');
      const wasmBuffer = await wasmResponse.arrayBuffer();
      this.wasmModule = await WebAssembly.compile(wasmBuffer);

      // Generate track UI
      this.uiController.generateTrackUI(TRACK_NAMES);

      // Update UI with constants
      // NOTE: When all tracks share the same BPM (100 BPM), each track can loop
      // independently at its natural boundary without synchronization issues.
      // The trackLength.syncLength is extracted per-track from AudioTrackLength during audio loading.
      this.uiController.updateConstantsDisplay({
        beatsPerTrack: 0, // Individual per track
        syncLengthSamples: 0 // Individual per track from AudioTrackLength metadata
      });

      // Initialize audio engines
      console.log('🎵 Initializing audio engines on page load...');
      await this.initializeAudioEngines();

      // Update track statuses
      for (let i = 0; i < TRACK_URLS.length; i++) {
        this.uiController.updateTrackStatus(i, 'ready');
      }

      // Mark all tracks as loaded in UI
      this.uiController.markAllTracksLoaded();
    } catch (error) {
      console.error('WASM loading error:', error);
      alert('Error loading WebAssembly module. Check console for details.');
    }
  }

  /**
   * Initialize audio engines
   */
  private async initializeAudioEngines(): Promise<void> {
    // Create audio context
    this.audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
    console.log('📱 AudioContext created, state:', this.audioContext.state);

    if (this.audioContext.state === 'suspended') {
      console.log('⏸️ AudioContext is suspended, will resume on first user interaction');
    }

    // Initialize audio coordinator
    await this.audioCoordinator.initialize({
      audioContext: this.audioContext,
      wasmModule: this.wasmModule!,
      trackUrls: TRACK_URLS,
      initialTempo: INITIAL_TEMPO
    });

    // Update track statuses as they load
    for (let i = 0; i < TRACK_URLS.length; i++) {
      this.uiController.updateTrackStatus(i, 'loading');
    }

    // Wait for all tracks to be ready
    for (let i = 0; i < TRACK_URLS.length; i++) {
      this.uiController.updateTrackStatus(i, 'ready');
    }
  }

  /**
   * Initialize UI
   */
  private initializeUI(): void {
    this.uiController.initialize();
    this.initialize();
  }

  /**
   * Handle play toggle
   */
  private async handlePlayToggle(): Promise<void> {
    if (!this.audioCoordinator.isPlaying()) {
      await this.playAll();
    } else {
      await this.pauseAll();
    }
  }

  /**
   * Handle tempo change
   */
  private handleTempoChange(tempo: number): void {
    this.audioCoordinator.updateTempo(tempo);
  }

  /**
   * Handle track mute toggle
   */
  private async handleTrackMute(trackIndex: number): Promise<void> {
    const isMuted = this.trackManager.toggleMute(trackIndex);

    if (!isMuted) {
      // Unmuted - check if this is first unmute
      if (!this.trackManager.hasStartedPlayback()) {
        this.trackManager.setPlaybackStarted(true);
        await this.playAll();
        this.uiController.showPlayButton();
      }

      // Set volume
      const volume = this.trackManager.getPreviousVolume(trackIndex);
      this.audioCoordinator.setTrackVolume(trackIndex, volume);
    } else {
      // Muted - set volume to 0
      this.audioCoordinator.setTrackVolume(trackIndex, 0);
    }

    // Update UI
    this.uiController.updateTrackMuteState(trackIndex, isMuted);
  }

  /**
   * Play all tracks
   */
  private async playAll(): Promise<void> {
    try {
      const overallMeasure = performanceMonitor.startMeasurement('playAll-total');

      // Check if we're resuming from pause or starting fresh
      if (this.audioCoordinator.isPaused()) {
        // Just resume - don't call play() again to maintain synchronization
        await this.audioCoordinator.resumeAll();
      } else {
        // Starting fresh
        // Verify all tracks are ready
        if (!this.audioCoordinator.areAllTracksReady()) {
          console.error('❌ Not all tracks are ready!');
          alert('Some tracks are not fully loaded. Please try again.');
          return;
        }

        // Play all tracks using individual metadata
        // NOTE: Each track's length (beats and samples) is extracted from its M4A metadata
        // The AudioEngineCoordinator will get individual track metadata and use correct values
        await this.audioCoordinator.playAll({
          lengthInBeats: 0, // Will be overridden by individual track metadata
          lengthInSamples: 0, // Will be overridden by individual track metadata
          timeSignature: 4
        });
      }

      // Update UI
      this.uiController.updatePlayButton(true);

      // Update track statuses
      for (let i = 0; i < this.audioCoordinator.getEngineCount(); i++) {
        this.uiController.updateTrackStatus(i, 'playing');
      }

      // Start animations
      this.startAnimations();

      performanceMonitor.endMeasurement(overallMeasure);

      // Measure audio latency
      if (this.audioContext) {
        performanceMonitor.measureAudioLatency(this.audioContext);
      }
    } catch (error) {
      console.error('Error playing tracks:', error);
      alert('Error starting playback. Check console for details.');
    }
  }

  /**
   * Pause all tracks
   */
  private async pauseAll(): Promise<void> {
    try {
      await this.audioCoordinator.pauseAll();

      this.uiController.updatePlayButton(false);

      // Update track statuses
      for (let i = 0; i < this.audioCoordinator.getEngineCount(); i++) {
        this.uiController.updateTrackStatus(i, 'paused');
      }

      this.stopAnimations();
    } catch (error) {
      console.error('Error pausing tracks:', error);
    }
  }

  /**
   * Stop all tracks
   */
  private async stopAll(): Promise<void> {
    try {
      await this.audioCoordinator.stopAll();

      this.trackManager.resetPlaybackState();
      this.uiController.updatePlayButton(false);
      this.uiController.hidePlayButton();

      // Reset all tracks to muted
      this.trackManager.resetAllMuted();
      this.uiController.resetAllTracksMuted();

      // Reset volumes
      for (let i = 0; i < this.audioCoordinator.getEngineCount(); i++) {
        this.audioCoordinator.setTrackVolume(i, 0);
        this.uiController.updateTrackStatus(i, 'stopped');
      }

      this.stopAnimations();
      this.uiController.updateBeatDisplay(1);
    } catch (error) {
      console.error('Error stopping tracks:', error);
    }
  }

  /**
   * Start animations
   */
  private startAnimations(): void {
    const engine = this.audioCoordinator.getEngine(0);
    if (engine) {
      this.stateManager.updateBeatCounter(
        engine,
        this.audioCoordinator.isPlaying(),
        {
          beatsPerTrack: 0, // Individual per track from metadata
          currentTempo: this.audioCoordinator.getCurrentTempo(),
          sampleRate: SAMPLE_RATE,
          // NOTE: Each track loops based on its own trackLength.syncLength.
          // UI now shows individual track lengths from AudioTrackLength.
          syncLengthSamples: 0 // Individual per track from AudioTrackLength
        },
        this.audioContext || undefined
      );
      this.stateManager.startBeatCounter();
    }

    this.stateManager.updateLevelMeters(
      this.audioCoordinator.getAnalyserNodes(),
      this.audioCoordinator.getMeterData(),
      this.audioCoordinator.isPlaying()
    );
    this.stateManager.startLevelMeters();
  }

  /**
   * Stop animations
   */
  private stopAnimations(): void {
    this.stateManager.stopBeatCounter();
    this.stateManager.stopLevelMeters();
    this.uiController.resetLevelMeters();
  }
}

// Export the class for testing
export { MultiTrackPlayer };

// Initialize the player when the page loads
document.addEventListener('DOMContentLoaded', () => {
  new MultiTrackPlayer();

  // Expose performance monitoring commands to console
  const perfMon: PerfMonInterface = {
    start: (): void => {
      performanceMonitor.startMonitoring();
      console.log('Performance monitoring started');
    },
    stop: (): void => {
      performanceMonitor.stopMonitoring();
      console.log('Performance monitoring stopped');
    },
    report: (): void => {
      performanceMonitor.logReport();
    },
    reset: (): void => {
      (performanceMonitor as any).metrics = {
        cpuTime: [],
        memoryUsage: [],
        gcCount: 0,
        audioLatency: [],
        frameDrops: 0,
        startTime: null
      };
      console.log('Performance metrics reset');
    },
    poolStats: async (): Promise<void> => {
      try {
        const { globalBufferPool } = await import('./audio-engine/memory/buffer-pool.js');
        const { channelDataPool } = await import('./audio-engine/memory/channel-data-pool.js');

        console.log('=== Buffer Pool Statistics ===');
        console.log('Uint8Array Pool:', (globalBufferPool as any).getStatistics());
        console.log('Channel Data Pool:', (channelDataPool as any).getStatistics());
      } catch {
        console.log('Buffer pooling not available or not enabled');
      }
    }
  };

  // Add perfMon to global window interface
  (window as any).perfMon = perfMon;

  console.log('Performance monitoring available. Use:');
  console.log('  perfMon.start() - Start monitoring');
  console.log('  perfMon.stop()  - Stop monitoring');
  console.log('  perfMon.report() - Show report');
  console.log('  perfMon.reset() - Reset metrics');
  console.log('  perfMon.poolStats() - Show buffer pool statistics');
});
